import type { ApiResponse } from '@/types/api.types'
import type {
  ItemPlan,
  ItemPlanCreate,
  ItemPlanGantt,
  ItemPlanUpdate,
  PlanImplementacion,
  PlanImplementacionUpdate,
  PresupuestoFuncion,
  PresupuestoMes,
  PresupuestoResumen,
  SyncPreview,
  TipoItemPlan
} from '@/types/planning.types'

import { apiClient, ensureValidToken } from '../client'

class PlanningServiceClass {
  private BASE = '/api/v1/assessments'

  // --- Plan ---

  async getPlan(assessmentId: string): Promise<PlanImplementacion> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<PlanImplementacion>>(
      `${this.BASE}/${assessmentId}/plan`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getPlan')
  }

  async updatePlan(
    assessmentId: string,
    data: PlanImplementacionUpdate
  ): Promise<PlanImplementacion> {
    await ensureValidToken()
    const response = await apiClient.patch<ApiResponse<PlanImplementacion>>(
      `${this.BASE}/${assessmentId}/plan`,
      data
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from updatePlan')
  }

  // --- Items ---

  async getItems(assessmentId: string, tipoItem?: TipoItemPlan): Promise<ItemPlan[]> {
    await ensureValidToken()
    const params = tipoItem ? { tipo_item: tipoItem } : {}
    const response = await apiClient.get<ApiResponse<ItemPlan[]>>(
      `${this.BASE}/${assessmentId}/plan/items`,
      { params }
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getItems')
  }

  async createItem(assessmentId: string, data: ItemPlanCreate): Promise<ItemPlan> {
    await ensureValidToken()
    const response = await apiClient.post<ApiResponse<ItemPlan>>(
      `${this.BASE}/${assessmentId}/plan/items`,
      data
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from createItem')
  }

  async updateItem(assessmentId: string, itemId: string, data: ItemPlanUpdate): Promise<ItemPlan> {
    await ensureValidToken()
    const response = await apiClient.patch<ApiResponse<ItemPlan>>(
      `${this.BASE}/${assessmentId}/plan/items/${itemId}`,
      data
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from updateItem')
  }

  async deleteItem(assessmentId: string, itemId: string): Promise<void> {
    await ensureValidToken()
    await apiClient.delete(`${this.BASE}/${assessmentId}/plan/items/${itemId}`)
  }

  // --- Sincronización ---

  async syncRecommendations(
    assessmentId: string,
    recomendacionIds?: string[]
  ): Promise<ItemPlan[]> {
    await ensureValidToken()
    const response = await apiClient.post<ApiResponse<ItemPlan[]>>(
      `${this.BASE}/${assessmentId}/plan/sync-recommendations`,
      recomendacionIds ? { recomendacion_ids: recomendacionIds } : {}
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from syncRecommendations')
  }

  async getSyncPreview(assessmentId: string): Promise<SyncPreview> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<SyncPreview>>(
      `${this.BASE}/${assessmentId}/plan/sync-preview`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getSyncPreview')
  }

  // --- Presupuesto ---

  async getBudgetSummary(assessmentId: string): Promise<PresupuestoResumen> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<PresupuestoResumen>>(
      `${this.BASE}/${assessmentId}/plan/budget/summary`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getBudgetSummary')
  }

  async getBudgetByFunction(assessmentId: string): Promise<PresupuestoFuncion[]> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<PresupuestoFuncion[]>>(
      `${this.BASE}/${assessmentId}/plan/budget/by-function`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getBudgetByFunction')
  }

  async getMonthlyDistribution(assessmentId: string): Promise<PresupuestoMes[]> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<PresupuestoMes[]>>(
      `${this.BASE}/${assessmentId}/plan/budget/monthly`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getMonthlyDistribution')
  }

  // --- Gantt ---

  async getGanttData(assessmentId: string): Promise<ItemPlanGantt[]> {
    await ensureValidToken()
    const response = await apiClient.get<ApiResponse<ItemPlanGantt[]>>(
      `${this.BASE}/${assessmentId}/plan/gantt`
    )
    if (response.data?.data) return response.data.data
    throw new Error('Invalid response from getGanttData')
  }
}

export const planningService = new PlanningServiceClass()
